import {
  createMatterShareFolder,
  accessMatterShareFolder,
  createMatterShareFolderFile,
  deleteMatterShareFolderFile,
  deleteMatterShareFolder,
} from "../../requests/matters/matter-share-folder";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIError } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "react-hot-toast";

export const useCreateMatterShareFolder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createMatterShareFolder,

    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error creating matter share folder");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Matter share folder created successfully");
    },
  });
};

export const useAccessMatterShareFolder = (data: {
  shareFolderPasscode: string;
}) => {
  return {
    queryKey: [queryKeys.matterShareFolders, data.shareFolderPasscode],
    queryFn: () => accessMatterShareFolder(data),
    enabled: !!data.shareFolderPasscode,
    staleTime: 0,
    retry: (failureCount: number, error: unknown) => {
      // Don't retry on 401 (unauthorized) errors - these are likely invalid passcodes
      if (error instanceof AxiosError && error.response?.status === 403) {
        return false;
      }
      // Retry other errors up to 2 times
      return failureCount < 2;
    },
  };
};

export const useDeleteMatterShareFolder = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteMatterShareFolder,
    onError: (error: AxiosError<APIError>) => {
      if (showToast) {
        toastAPIErrorMessage(error, "Error deleting matter share folder");
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Matter share folder deleted successfully");
      }
    },
  });
};

export const useCreateMatterShareFolderFile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createMatterShareFolderFile,

    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error creating matter share folder file");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Matter share folder file created successfully");
    },
  });
};

export const useDeleteMatterShareFolderFile = ({
  showToast = true,
}: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteMatterShareFolderFile,
    onError: (error: AxiosError<APIError>) => {
      if (showToast) {
        toastAPIErrorMessage(error, "Error deleting matter share folder file");
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      if (showToast) {
        toast.success("Matter share folder file deleted successfully");
      }
    },
  });
};
